import { RuleObject } from 'antd/lib/form';
import cardValidator from 'card-validator';
import {
    isValidCPF,
    isValidCNPJ,
    isValidCEP,
    isValidMobilePhone,
    isValidPhone,
} from '@brazilian-utils/brazilian-utils';
import { fromDateShort, fromDateTimeShort } from './dates';
import { DateTime } from 'luxon';
import { EMAIL_REGEX } from './strings';

export type Validator<T = string> = (
    message: string
) => (object: RuleObject, input: T) => Promise<void>;

const createValidator = <T>(predicate: (input: T) => boolean): Validator<T> => {
    return (message: string) => {
        return (_: RuleObject, input: T) => {
            return predicate(input)
                ? Promise.resolve()
                : Promise.reject(message);
        };
    };
};

const createAsyncValidator = <T>(
    predicate: (input: T) => Promise<boolean>
): Validator<T> => {
    return (message: string) => {
        return async (_: RuleObject, input: T) => {
            return (await predicate(input))
                ? Promise.resolve()
                : Promise.reject(message);
        };
    };
};

const cardNumber = createValidator(x => cardValidator.number(x).isValid);

const cardExpirationDate = createValidator(
    x => cardValidator.expirationDate(x).isValid
);

const date = createValidator<string>((date: string) =>
    !date ? false : fromDateShort(date).isValid
);

const dateTime = createValidator<string>((dateTime: string) =>
    !dateTime ? false : fromDateTimeShort(dateTime).isValid
);

const pastDateTime = createValidator<string>((dateTime: string) =>
    !dateTime
        ? false
        : fromDateTimeShort(dateTime).isValid &&
          fromDateTimeShort(dateTime).diffNow().toMillis() <= 0
);

const futureDateTime = createValidator<string>((dateTime: string) =>
    !dateTime
        ? false
        : fromDateTimeShort(dateTime).isValid &&
          fromDateTimeShort(dateTime).diffNow().toMillis() > 0
);

const birthDate = createValidator<string>((date: string) =>
    !date
        ? false
        : fromDateShort(date).isValid &&
          fromDateShort(date).diffNow().toMillis() <= 0
);

const after = (afterDate?: DateTime): Validator<string> =>
    createValidator<string>((date: string) =>
        !date
            ? false
            : fromDateShort(date).isValid &&
              fromDateShort(date)
                  .diff(afterDate ?? DateTime.now())
                  .toMillis() > 0
    );

const before = (beforeDate?: DateTime): Validator<string> =>
    createValidator<string>((date: string) =>
        !date
            ? false
            : fromDateShort(date).isValid &&
              fromDateShort(date)
                  .diff(beforeDate ?? DateTime.now())
                  .toMillis() < 0
    );

const between = (
    afterDate: DateTime,
    beforeDate: DateTime
): Validator<string> =>
    createValidator<string>((date: string) => {
        if (!date) return false;
        const converted = fromDateShort(date);
        return (
            converted.isValid &&
            converted.diff(beforeDate).toMillis() < 0 &&
            converted.diff(afterDate).toMillis() > 0
        );
    });

const futureDate = createValidator<string>((date: string) =>
    !date ? false : fromDateShort(date).diffNow().toMillis() >= 0
);

const cpf = createValidator(isValidCPF);
const email = (blockPlusSign: boolean) =>
    createValidator<string>(
        email =>
            Boolean(email.match(EMAIL_REGEX)) &&
            (!blockPlusSign ||
                email.includes('@alude.com.br') ||
                !email.includes('+'))
    );
const cnpj = createValidator(isValidCNPJ);
const cep = createValidator(isValidCEP);
const cellphone = createValidator(isValidMobilePhone);
const phoneCellphone = createValidator(isValidPhone);

const step = (step: number): Validator<number> =>
    createValidator<number>(value => value % step === 0);

const max = (max: number): Validator<number> =>
    createValidator<number>(value => value <= max);

const min = (min: number): Validator<number> =>
    createValidator<number>(value => value >= min);

const notEqualTo = (num: number): Validator<number> =>
    createValidator<number>(value => value !== num);

const url = createValidator<string>(url => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
});

export { createValidator, createAsyncValidator };
export default {
    after,
    before,
    birthDate,
    cardExpirationDate,
    cardNumber,
    dateTime,
    email,
    pastDateTime,
    notEqualTo,
    cep,
    cnpj,
    cpf,
    cellphone,
    phoneCellphone,
    date,
    between,
    futureDate,
    futureDateTime,
    step,
    max,
    min,
    url,
};
